<template>
  <VueFinalModal
    v-bind="filteredAttrs"
    class="ccc-modal-container"
    :content-class="computedContentClass"
    content-transition="vfm-fade"
    overlay-transition="vfm-fade"
    :prevent-click="false"
    teleport-to="body"
    :zIndexFn="zIndexFn"
    :content-style="computedContentStyle"
    :fit-parent="false"
    :esc-to-close="true"
    :focus-trap="focusTrap"
  >
    <slot />
  </VueFinalModal>
</template>

<script setup>
import { computed, useAttrs } from "vue";
import { VueFinalModal } from "vue-final-modal";

defineOptions({ inheritAttrs: false });

const zIndexFn = ({ index }) => 3000 + 2 * index;
const attrs = useAttrs();

const ignoredAttrs = [
  "width",
  "height",
  "name",
  "transition",
  "adaptive",
  "resizable",
  "scrollable",
  "focus-trap",
  "focusTrap",
  "content-class",
  "contentClass",
];

// Trap focus only when a modal asks for it, as before the move to vue-final-modal;
// its default trap pulls focus back from anything outside, such as Oliver's input.
const focusTrap = computed(() => {
  const requested = attrs["focus-trap"] ?? attrs.focusTrap;
  return requested === true || requested === ""
    ? { allowOutsideClick: true }
    : false;
});

const filteredAttrs = computed(() => {
  const result = {};
  for (const key in attrs) {
    if (!ignoredAttrs.includes(key)) {
      result[key] = attrs[key];
    }
  }
  return result;
});

const computedContentClass = computed(() => {
  const classes = ["ccc-modal-content", "v--modal"];
  const extra = attrs["content-class"] || attrs["contentClass"];
  if (extra) {
    classes.push(extra);
  }
  return classes.join(" ");
});

const computedContentStyle = computed(() => {
  const style = {};
  const w = attrs.width;
  const h = attrs.height;
  const scrollable = attrs["scrollable"] === true || attrs["scrollable"] === "";

  if (w) {
    style.width = typeof w === "number" || /^\d+$/.test(w) ? `${w}px` : w;
  }

  if (h) {
    const heightVal = typeof h === "number" || /^\d+$/.test(h) ? `${h}px` : h;
    style.maxHeight = heightVal;
    style.height = scrollable ? heightVal : "auto";
    style.overflowY = "auto";
  } else {
    style.height = "auto";
    style.overflowY = scrollable ? "auto" : "visible";
  }

  return style;
});
</script>

<style>
.ccc-modal-container {
  display: flex;
  justify-content: center;
  align-items: center;
}

.ccc-modal-content {
  position: relative;
  box-sizing: border-box;
  background-color: var(--body-bg-color, white);
  color: var(--main-text-color, inherit);
  border-radius: 3px;
  width: 600px;
  max-height: calc(100vh - 2rem);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

@media (max-width: 768px) {
  .ccc-modal-content {
    max-width: calc(100vw - 1rem);
    max-height: calc(100vh - 1rem);
  }
}

@media (max-width: 480px) {
  .ccc-modal-content {
    max-width: calc(100vw - 1rem);
    max-height: calc(100vh - 1rem);
  }
}
</style>
